<script>
    import { onMount } from 'svelte';
    import "ol/ol.css";
    import Map from "ol/Map";
    import View from "ol/View";
    import TileLayer from "ol/layer/Tile";
    import OSM from "ol/source/OSM";
    import ImageLayer from 'ol/layer/Image';
    import ImageArcGISRest from 'ol/source/ImageArcGISRest';
    import ImageWMS from 'ol/source/ImageWMS';
    import { boundingExtent } from 'ol/extent';
    import { fromLonLat, transformExtent } from "ol/proj";
    import WMSCapabilities from 'ol/format/WMSCapabilities';
    import { slide } from 'svelte/transition';
    import { quintOut } from 'svelte/easing';
    import BingMaps from 'ol/source/BingMaps';

    export let dataset;
    
    let selectedLayer = dataset?.mapservice_rest_url[0]; // Default to first layer
    let mapElement;
    let map;

    let legendData = {};
    let legendExpanded = true;
    let layersExpanded = true;
    let baseLayer = 'osm';
    let lcLayerVisible = true;

    let layerVisibility = {};
    let layerInstances = {};

    // Initialize visibility and layer instances when dataset changes
    $: if (dataset?.mapservice_rest_url) {
        dataset.mapservice_rest_url.forEach(layer => {
            const uniqueKey = `${layer.url}_${layer.layerIndex}`;
            layerVisibility[uniqueKey] = true;
            layerInstances[uniqueKey] = createLayer(layer);
        });
    }

    // Create a function to create layers
    function createLayer(layerConfig) {
        const uniqueKey = `${layerConfig.url}_${layerConfig.layerIndex}`;
        
        if (layerConfig.mapserver === 'arcgis') {
            return new ImageLayer({
                visible: true,
                zIndex: 9,
                source: new ImageArcGISRest({
                    url: layerConfig.url,
                    crossOrigin: 'anonymous',
                    params: {
                        LAYERS: `show:${layerConfig.layerIndex}`,
                        FORMAT: "image/png"
                    }
                })
            });
        } else {
            // Modified GeoServer WMS layer creation
            return new ImageLayer({
                visible: true,
                zIndex: 9,
                source: new ImageWMS({
                    url: layerConfig.url,
                    crossOrigin: 'anonymous',
                    params: {
                        'LAYERS': layerConfig.layerIndex,
                        'FORMAT': 'image/png',
                        'VERSION': '1.1.1',
                        'TILED': true
                    },
                    serverType: 'geoserver'
                })
            });
        }
    }

    // Land cover layer
    $: lcLayer = selectedLayer?.mapserver === 'arcgis' 
        ? new ImageLayer({
            visible: true,
            zIndex:9,
            source: new ImageArcGISRest({
                url: selectedLayer?.url || '',
                crossOrigin: 'anonymous',
                params: {
                    LAYERS: `show:${selectedLayer?.layerIndex}`,
                    FORMAT: "image/png"
                }
            })
        })
        : new ImageLayer({
            visible: true,
            zIndex:9,
            source: new ImageWMS({
                url: selectedLayer?.url || '',
                params: {
                    LAYERS: selectedLayer?.layerIndex,
                    TILED: true,
                    serverType: 'geoserver',
                },
            })
        });

    // Create base layers
    const osmLayer = new TileLayer({
        source: new OSM(),
    });

    const bingLayer = new TileLayer({
        source: new BingMaps({
            key: 'ApTJzdkyN1DdFKkRAE6QIDtzihNaf6IWJsT-nQ_2eMoO4PN__0Tzhl2-WgJtXFSp',
            imagerySet: 'AerialWithLabels',
        }),
        visible: false
    });

    function createMap() {
        const allLayers = [osmLayer, bingLayer];
        
        // Add all dataset layers
        dataset.mapservice_rest_url.forEach(layer => {
            const uniqueKey = `${layer.url}_${layer.layerIndex}`;
            allLayers.push(layerInstances[uniqueKey]);
        });

        map = new Map({
            target: "detail-map",
            layers: allLayers,
            view: new View({
                center: fromLonLat([82.9492797851563, 27.6383056640625]),
                zoom: 4
            }),
        });

        zoomExtent();
    }

    function zoomExtent() {
        if (!selectedLayer) {
            console.error('No layer selected');
            return;
        }

        if (selectedLayer.mapserver === 'arcgis') {
            // Create a new layer specifically for getting the extent
            const tempLayer = selectedLayer.mapserver === 'arcgis' 
                ? new ImageLayer({
                    source: new ImageArcGISRest({
                        url: selectedLayer.url,
                        crossOrigin: 'anonymous',
                        params: {
                            LAYERS: `show:${selectedLayer.layerIndex}`,
                            FORMAT: "image/png"
                        }
                    })
                })
                : new ImageLayer({
                    source: new ImageWMS({
                        url: selectedLayer.url,
                        params: {
                            LAYERS: selectedLayer.layerIndex,
                            TILED: true,
                            serverType: 'geoserver',
                        }
                    })
                });

            // Add the temporary layer to the map
            map.addLayer(tempLayer);
            
            const layerSource = tempLayer.getSource();
            
            layerSource.once('imageloadend', function() {
                console.log("layerSource", layerSource);
                fetch(`${selectedLayer.url}?f=json`)
                    .then(response => response.json())
                    .then(data => {
                        if (data.fullExtent) {
                            const extent = transformExtent(
                                [
                                    data.fullExtent.xmin,
                                    data.fullExtent.ymin,
                                    data.fullExtent.xmax,
                                    data.fullExtent.ymax
                                ],
                                'EPSG:4326',
                                'EPSG:3857'
                            );

                            if (extent.every(coord => typeof coord === 'number' && !isNaN(coord))) {
                                map.getView().fit(extent, {
                                    size: map.getSize(),
                                    duration: 1000,
                                    padding: [50, 50, 50, 50]
                                });
                            }
                        }
                        // Remove the temporary layer after zooming
                        map.removeLayer(tempLayer);
                    })
                    .catch(error => {
                        console.error('Error fetching ArcGIS layer info:', error);
                        setDefaultExtent();
                        map.removeLayer(tempLayer);
                    });
            });

            // Force the layer to load
            layerSource.refresh();
        } else {
            // Modified GeoServer WMS capabilities handling
            const parser = new WMSCapabilities();
            const baseUrl = selectedLayer.url;
            
            if (!baseUrl) {
                console.error('No URL provided for WMS layer');
                return;
            }

            fetch(`${baseUrl}?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetCapabilities`)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    return response.text();
                })
                .then(text => {
                    try {
                        const result = parser.read(text);
                        // Find the layer recursively
                        function findLayer(layers, targetName) {
                            for (let layer of layers) {
                                if (layer.Name === targetName) {
                                    return layer;
                                }
                                if (layer.Layer) {
                                    const found = findLayer(Array.isArray(layer.Layer) ? layer.Layer : [layer.Layer], targetName);
                                    if (found) return found;
                                }
                            }
                            return null;
                        }

                        const layers = result.Capability.Layer.Layer;
                        const targetLayer = findLayer(Array.isArray(layers) ? layers : [layers], selectedLayer.layerIndex);

                        if (!targetLayer) {
                            console.error('Layer not found in capabilities');
                            setDefaultExtent();
                            return;
                        }

                        // Try different bounding box properties
                        let extent;
                        if (targetLayer.EX_GeographicBoundingBox) {
                            extent = targetLayer.EX_GeographicBoundingBox;
                        } else if (targetLayer.BoundingBox) {
                            const bbox = targetLayer.BoundingBox.find(box => box.crs === 'EPSG:4326' || box.crs === 'CRS:84');
                            if (bbox) {
                                extent = [bbox.extent[0], bbox.extent[1], bbox.extent[2], bbox.extent[3]];
                            }
                        }

                        if (!extent) {
                            console.warn('No bounding box found, using default extent');
                            setDefaultExtent();
                            return;
                        }

                        const transformedExtent = transformExtent(
                            extent,
                            'EPSG:4326',
                            'EPSG:3857'
                        );

                        map.getView().fit(transformedExtent, {
                            size: map.getSize(),
                            duration: 1000,
                            padding: [50, 50, 50, 50]
                        });
                    } catch (parseError) {
                        console.error('Error parsing WMS capabilities:', parseError);
                        setDefaultExtent();
                    }
                })
                .catch(error => {
                    console.error('Error fetching WMS capabilities:', error);
                    setDefaultExtent();
                });
        }
    }

    // Add a helper function for setting default extent
    function setDefaultExtent() {
        // Default to HKH region extent
        const defaultExtent = transformExtent(
            [60, 15, 105, 40], // Approximate HKH region bounds [min_lon, min_lat, max_lon, max_lat]
            'EPSG:4326',
            'EPSG:3857'
        );
        
        map.getView().fit(defaultExtent, {
            size: map.getSize(),
            duration: 1000,
            padding: [50, 50, 50, 50]
        });
    }

    async function fetchLegend() {
        // Reset legendData as an object instead of an array
        legendData = {};
        
        for (const layer of dataset.mapservice_rest_url) {
            if (layer.mapserver === 'arcgis') {
                const legendUrl = `${layer.url}/legend?f=json`;
                try {
                    const response = await fetch(legendUrl);
                    const data = await response.json();
                    
                    const targetLayerId = parseInt(layer.layerIndex);
                    const layerLegend = data.layers?.find(l => l.layerId === targetLayerId);

                    if (layerLegend) {
                        // Store legend items under a unique key combining URL and layer index
                        const uniqueKey = `${layer.url}_${layer.layerIndex}`;
                        legendData[uniqueKey] = {
                            name: layer.name,
                            items: layerLegend.legend.map(item => ({
                                label: item.label,
                                imageData: `data:image/png;base64,${item.imageData}`
                            }))
                        };
                    }
                } catch (error) {
                    console.error('Error fetching legend:', error);
                }
            } else {
                // Handle WMS layers
                const legendUrl = `${layer.url}?REQUEST=GetLegendGraphic&VERSION=1.0.0&FORMAT=image/png&WIDTH=20&HEIGHT=20&LAYER=${layer.layerIndex}`;
                const uniqueKey = `${layer.url}_${layer.layerIndex}`;
                legendData[uniqueKey] = {
                    name: layer.name,
                    items: [{
                        label: layer.name,
                        imageUrl: legendUrl
                    }]
                };
            }
        }
    }

    $: if (dataset?.mapservice_rest_url) {
        fetchLegend();
    }

    function toggleBaseLayer(selected) {
        baseLayer = selected;
        osmLayer.setVisible(selected === 'osm');
        bingLayer.setVisible(selected === 'bing');
    }

    function toggleLcLayer() {
        lcLayerVisible = !lcLayerVisible;
        lcLayer.setVisible(lcLayerVisible);
    }

    onMount(() => {
        if (mapElement) {
            createMap();
        }
        
        return () => {
            if (map) {
                map.setTarget(null);
                map.dispose();  // Properly dispose of the map
                map = null;
            }
        };
    });
</script>

<div class="w-full h-[60vh] relative">
    <!-- https://geoapps.icimod.org/icimodarcgis/rest/services/HKH/Landcover/MapServer -->
    <!-- {dataset.mapservice_rest_url } -->
    <div id="detail-map" bind:this={mapElement} class="w-full h-full shadow-lg rounded-lg"></div>
    
    <!-- Layers Button -->
    <div class="absolute bottom-4 left-4">
        <button 
            class="bg-white p-2 rounded-lg shadow-lg mb-2 w-full flex items-center justify-between text-sm hover:bg-gray-50"
            on:click={() => layersExpanded = !layersExpanded}
        >
            <span class="font-semibold">Layers</span>
            <svg 
                class="w-4 h-4 transform transition-transform duration-300 {layersExpanded ? 'rotate-180' : ''}" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
            >
                <path 
                    stroke-linecap="round" 
                    stroke-linejoin="round" 
                    stroke-width="2" 
                    d="M19 9l-7 7-7-7"
                />
            </svg>
        </button>

        <!-- Layers Content -->
        {#if layersExpanded}
            <div 
                class="bg-white border rounded-lg shadow-lg p-2 min-w-[200px]"
                transition:slide={{ duration: 300, easing: quintOut }}
            >
                <!-- Base Layers -->
                <div class="mb-3">
                    <h3 class="font-semibold mb-2 text-sm">Base Map</h3>
                    <div class="space-y-2">
                        <label class="flex items-center space-x-2">
                            <input 
                                type="radio" 
                                name="baseLayer" 
                                value="osm" 
                                checked={baseLayer === 'osm'}
                                on:change={() => toggleBaseLayer('osm')}
                            >
                            <span class="text-sm">OpenStreetMap</span>
                        </label>
                        <label class="flex items-center space-x-2">
                            <input 
                                type="radio" 
                                name="baseLayer" 
                                value="bing" 
                                checked={baseLayer === 'bing'}
                                on:change={() => toggleBaseLayer('bing')}
                            >
                            <span class="text-sm">Bing Aerial</span>
                        </label>
                    </div>
                </div>

                <!-- Overlay Layers -->
                <div>
                    <h3 class="font-semibold mb-2 text-sm">Overlay Layers</h3>
                    {#each dataset.mapservice_rest_url as layer}
                        <label class="flex items-center space-x-2 mb-2">
                            <input 
                                type="checkbox" 
                                checked={layerVisibility[`${layer.url}_${layer.layerIndex}`]}
                                on:change={() => {
                                    const uniqueKey = `${layer.url}_${layer.layerIndex}`;
                                    layerVisibility[uniqueKey] = !layerVisibility[uniqueKey];
                                    layerInstances[uniqueKey].setVisible(layerVisibility[uniqueKey]);
                                }}
                            >
                            <span class="text-sm">{layer.name}</span>
                        </label>
                    {/each}
                </div>

            </div>
        {/if}
    </div>

    <!-- Legend -->
    {#if Object.keys(legendData).length > 0}
        <div class="absolute bottom-4 right-4">
            <!-- Legend Button -->
            <button 
                class="bg-white p-2 rounded-lg shadow-lg mb-2 w-full flex items-center justify-between text-sm hover:bg-gray-50"
                on:click={() => legendExpanded = !legendExpanded}
            >
                <span class="font-semibold">Legend</span>
                <svg 
                    class="w-4 h-4 transform transition-transform duration-300 {legendExpanded ? 'rotate-180' : ''}" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                >
                    <path 
                        stroke-linecap="round" 
                        stroke-linejoin="round" 
                        stroke-width="2" 
                        d="M19 9l-7 7-7-7"
                    />
                </svg>
            </button>

            <!-- Legend Content -->
            {#if legendExpanded}
                <div 
                    class="bg-white border rounded-lg shadow-lg max-w-xs p-2"
                    transition:slide={{ duration: 300, easing: quintOut }}
                >
                    <div class="max-h-[300px] overflow-y-auto">
                        {#each Object.keys(legendData) as uniqueKey}
                            <div class="mb-4">
                                <h4 class="font-semibold text-sm mb-2">{legendData[uniqueKey].name}</h4>
                                <!-- Only show legend items that match the current layer's URL -->
                                {#each legendData[uniqueKey].items as item}
                                    <div class="flex items-center mb-2">
                                        {#if item.imageData}
                                            <img src='{item.imageData}' alt={item.label} class="mr-2" />
                                        {:else if item.imageUrl}
                                            <img src={item.imageUrl} alt={item.label} class="mr-2" />
                                        {/if}
                                        <span class="text-xs">{item.label}</span>
                                    </div>
                                {/each}
                            </div>
                        {/each}
                    </div>
                </div>
            {/if}
        </div>
    {/if}
</div>

<style>
    :global(.ol-zoom) {
        top: 1rem;
        left: 1rem;
    }
</style>
